import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import AddPopup from '../AddPopup';
import './index.scss';

export default function LeftBarUpload({ hideText, buttonSx = {}, iconSx = {} }) {
  const { t } = useTranslation("common");
  const history = useHistory();
  
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      setIsPopupOpen(false);
      setIsAddPopupOpen(false);
    };
  }, []);

  const togglePopup = () => {
    // Blur the currently focused element to prevent aria-hidden warning
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setIsPopupOpen((prev) => !prev);
  };

  const openAddPopup = () => {
    setIsAddPopupOpen(true);
    togglePopup();
  };

  const closeAddPopup = (e, close) => {
    setIsAddPopupOpen(false);
    // Ensure any lingering backdrop is removed
    const backdrops = document.querySelectorAll('.MuiBackdrop-root');
    backdrops.forEach(backdrop => {
      if (backdrop && backdrop.style.opacity === '0') {
        backdrop.remove();
      }
    });
  };

  const navigateTo = (path) => {
    togglePopup();
    setIsAddPopupOpen(false); // Ensure AddPopup is closed
    history.push(path);
  };

 return (
  <Box id="sideBarUpload" sx={{ p: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
   <Button
  onClick={togglePopup}
  sx={{
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    display: "flex",
    borderRadius: "8px",
    background: "none",
    border: "none",
    color: "var(--primary-color)",
    fontWeight: 500,
    fontSize: "14px",
    px: 2,
    py: 1.25,
    textTransform: "none",
    transition: "color 0.2s",
    '&:hover': {
      background: "none",
      border: "none",
      color: "var(--primary-color)",
      opacity: 0.8,
    },
    ...buttonSx
  }}
  fullWidth
>
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{
      width: 22,
      height: 22,
      marginRight: "12px",
      fill: "currentColor",
      display: "inline-block",
      verticalAlign: "middle",
      ...iconSx
    }}
  >
    {/* Circle */}
    <circle 
      cx="12" 
      cy="12" 
      r="11" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="1.5"
    />
    {/* Up Arrow */}
    <path 
      d="M12 16V8M12 8L9 11M12 8L15 11" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </Box>
  <Typography
    variant="body1"
    sx={{
      color: "inherit",
      fontWeight: 500,
      fontSize: "14px",
      letterSpacing: 0,
      display: "inline-block",
      verticalAlign: "middle",
    }}
  >
    {!hideText && t("Upload")}
  </Typography>
</Button>

    <Dialog 
      open={isPopupOpen} 
      onClose={togglePopup} 
      fullWidth 
      maxWidth="sm"
      disableEnforceFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          borderRadius: 16,
          padding: '20px',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.8)',
          overflow: 'visible',
          backgroundImage: 'none',
        }
      }}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white', textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' }}>
        {t("Create and Upload to SIKI!")}
      </DialogTitle>
      <DialogContent sx={{ paddingTop: '20px' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <Box
            onClick={openAddPopup}
            className="sidebar-button"
            sx={{
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              py: 1.5,
              borderRadius: 2,
              background: "var(--primary-color-light)",
              color: "var(--primary-color)",
              fontWeight: 700,
              fontSize: "1.1rem",
              mb: 1,
              '&:hover': {
                background: "var(--primary-color-hover)",
              },
            }}
          >
            <Typography variant="h6">{t("Create Playlist")}</Typography>
          </Box>
          <Box
            onClick={() => navigateTo('/app/UploadMusic')}
            className="sidebar-button"
            sx={{
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              py: 1.5,
              borderRadius: 2,
              background: "var(--primary-color-light)",
              color: "var(--primary-color)",
              fontWeight: 700,
              fontSize: "1.1rem",
              mb: 1,
              '&:hover': {
                background: "var(--primary-color-hover)",
              },
            }}
          >
            <Typography variant="h6">{t("Upload Music")}</Typography>
          </Box>
          <Box
            onClick={() => navigateTo('/app/UploadAlbum')}
            className="sidebar-button"
            sx={{
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              py: 1.5,
              borderRadius: 2,
              background: "var(--primary-color-light)",
              color: "var(--primary-color)",
              fontWeight: 700,
              fontSize: "1.1rem",
              mb: 1,
              '&:hover': {
                background: "var(--primary-color-hover)",
              },
            }}
          >
            <Typography variant="h6">{t("Upload Album")}</Typography>
          </Box>
          <Box
            onClick={() => navigateTo('/app/UploadVideo')}
            className="sidebar-button"
            sx={{
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              py: 1.5,
              borderRadius: 2,
              background: "var(--primary-color-light)",
              color: "var(--primary-color)",
              fontWeight: 700,
              fontSize: "1.1rem",
              '&:hover': {
                background: "var(--primary-color-hover)",
              },
            }}
          >
            <Typography variant="h6">{t("Upload Video")}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button 
          onClick={togglePopup} 
          sx={{ color: '#ccc', fontWeight: 'bold' }}
        >
          {t("Cancel")}
        </Button>
      </DialogActions>
    </Dialog>

    <AddPopup
      open={isAddPopupOpen}
      handleClose={closeAddPopup}
      title={t("Create Playlist")}
      api="playlist"
    />
  </Box>
);
}